import { ANCHOR_KIND, anchorSentence, HALF_TIME_WAVE_MIN, MATCH_EVENT } from './anchors';

// FanWave admin constants — mirror the backend's `constants/fanwave.ts` so the CMS
// validates inputs before they ever reach the API (the server re-validates too).
export const FANWAVE = {
  /**
   * Validation: minimum configurable tap target. 10, not the 500 older docs described — the API
   * has enforced 10 for a while and the spec was corrected to match. Expected to return to 500,
   * so prefer `resolveTapTargetBounds` (which lets the server override it) over reading this.
   */
  MIN_TAP_TARGET: 10,
  /** Validation: maximum configurable tap target. */
  MAX_TAP_TARGET: 1000000,
  /**
   * Recommended increment for tap targets (enforced as a step server-side). 10 for the same
   * reason as MIN_TAP_TARGET, and expected to return to 500 alongside it.
   */
  TAP_TARGET_INCREMENT: 10,
  /** Global default tap target applied to all teams unless overridden. */
  DEFAULT_TAP_TARGET: 2000,
  /** Default minutes FanWave stays active after Full-Time. */
  DEFAULT_POST_FT_MINUTES: 30
};

/**
 * Tap-target bounds in force for a channel. The resolved-config response is the preferred
 * source (GET /admin/fanwave/config) so a server-side bounds change lands in the CMS without
 * a release; today it carries no bounds, so the mirrored constants above stand in.
 */
export const resolveTapTargetBounds = (config) => ({
  min: config?.limits?.minTapTarget ?? FANWAVE.MIN_TAP_TARGET,
  max: config?.limits?.maxTapTarget ?? FANWAVE.MAX_TAP_TARGET,
  step: config?.limits?.tapTargetIncrement ?? FANWAVE.TAP_TARGET_INCREMENT
});

/**
 * Validates a tap target against the min/max/step in force (see `resolveTapTargetBounds`).
 * Returns an error message when invalid, or null when valid.
 */
export const validateTapTarget = (target, bounds) => {
  const { min, max, step } = bounds || resolveTapTargetBounds(null);
  const n = Number(target);
  if (!Number.isFinite(n)) return 'Tap target must be a number';
  if (n < min) return `Tap target must be at least ${min}`;
  if (n > max) return `Tap target must be at most ${max}`;
  if (n % step !== 0) return `Tap target must be a multiple of ${step}`;
  return null;
};

/**
 * Tap target above which an uncapped per-fan payout is worth warning the admin about (see
 * `rewardsWarnings`). Set at the ceiling that stood before the raise to 1,000,000: anything at
 * or below it is a target the platform already allowed, so nothing about it is new.
 */
export const HIGH_TAP_TARGET = 20000;

// Thousands separators for numbers quoted in labels, hints and warnings. Bounds copy runs to
// seven digits now that a tap target may be 1,000,000, unreadable as a bare run of zeros.
export const fmtCount = (n) => Number(n).toLocaleString('en-US');

/**
 * The prompt to set a per-fan tap quota when a high target meets repeat cycles: with cycles
 * repeating and no quota, the claim falls back to the COLLECTIVE target as one fan's tap ceiling,
 * so the most a single fan can take home scales with the target. Quantified in POINTS when the
 * cycle numbers allow it, since that is what actually gets paid out; falls back to the tap count
 * when they don't, or when a reward cap already bounds the points and a points figure here would
 * contradict it. See `rewardsWarnings`, which decides when this applies.
 */
export const uncappedPayoutWarning = (rewards, target) => {
  const taps = Number(target);
  const perCycle = Number(rewards.tapsPerCycle);
  const basePoints = Number(rewards.basePoints);
  const lead = `A ${fmtCount(taps)}-tap target with repeat cycles and no tap limit`;

  if (!rewards.progressiveEnabled && !rewards.capEnabled && perCycle > 0 && basePoints > 0) {
    const cycles = Math.floor(taps / perCycle);
    return `${lead} lets a single fan earn up to ${fmtCount(
      cycles * basePoints
    )} points on their own — ${fmtCount(cycles)} cycles of ${fmtCount(
      basePoints
    )}. Set a Tap Limit Per Fan unless one fan earning that much is intended.`;
  }
  return `${lead} lets a single fan tap up to ${fmtCount(
    taps
  )} times on their own, because the community target doubles as each fan's tap ceiling. Set a Tap Limit Per Fan to bound what one fan can claim.`;
};

/** Wave campaign type — mirrors the backend's `FanwaveMode` enum. */
export const FANWAVE_MODE = {
  GAME_DAY: 'game_day',
  NON_GAME_DAY: 'non_game_day'
};

/** Non-Game Day schedule lifecycle — mirrors `FanwaveScheduleStatus`. */
export const WAVE_STATUS = {
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
};

/**
 * Why a wave that can no longer run shows no tap target, for the hover on the dash standing in
 * for it. A wave that ran reports the target archived when it closed, so a missing one means the
 * value was never recorded — and the session that held it is long cleared, so it cannot be
 * recovered. Says so rather than leaving a bare dash, which reads as "no target was set".
 *
 * Returns undefined whenever there is nothing to explain (a target is present, or the wave has
 * yet to run and simply has no target of its own — the channel default applies).
 */
export const missingTapTargetReason = (wave) => {
  if (!wave || wave.tapTarget != null) return undefined;
  if (wave.status === WAVE_STATUS.COMPLETED) {
    return 'The tap target for this FanWave was never recorded.';
  }
  if (wave.status === WAVE_STATUS.CANCELLED || wave.status === WAVE_STATUS.EXPIRED) {
    return 'The tap target for this FanWave was never recorded.';
  }
  return undefined;
};

/**
 * How to read a row's `rewards`. OVERRIDE/SNAPSHOT come from GET /list; RESOLVED is set by
 * the CMS itself after PUT /update, whose response echoes fully-resolved rules for a wave
 * that may not have started yet (so neither "override" nor "snapshot" describes it).
 */
export const REWARDS_SOURCE = {
  OVERRIDE: 'override',
  SNAPSHOT: 'snapshot',
  RESOLVED: 'resolved'
};

/** Section 7 "After Limit Reached" — mirrors `FanwaveCapBehavior`. */
export const CAP_BEHAVIOR = {
  CONTINUE: 'continue',
  DISABLE: 'disable'
};

/** Section 6 community-bonus eligibility — mirrors `FanwaveBonusEligibility`. */
export const BONUS_ELIGIBILITY = {
  ALL: 'all',
  MIN_TAPS: 'min_taps'
};

// Reward-field bounds — mirror `fanwaveRewardsJoiSchema` so the CMS validates before the API.
export const REWARD_LIMITS = {
  MIN_TAPS_PER_CYCLE: 1,
  MAX_TAPS_PER_CYCLE: 1000,
  MAX_BASE_POINTS: 10000,
  MAX_MILESTONES: 20,
  MAX_HEADLINE_LEN: 60,
  MAX_GOAL_MESSAGE_LEN: 120,
  MAX_COMMUNITY_BONUS_POINTS: 10000,
  MAX_CAP_POINTS: 100000,
  MAX_RANK_REWARDS: 20,
  /**
   * Per-fan TAP quota (`maxTapsPerUser`): integer 0–10000, where 0 means uncapped.
   *
   * Deliberately NOT derived from `FANWAVE.MAX_TAP_TARGET`. The two shared a ceiling once and no
   * longer do: that one is a channel-wide goal (up to 1,000,000), this is one fan's budget.
   */
  MAX_TAPS_PER_USER: 10000
};

/**
 * Per-fan TAP quota — how many times ONE fan may tap in a wave. `0` (the default) is uncapped.
 *
 * A different ceiling from the reward cap (`capEnabled`/`maxPoints`/`capBehavior`), which bounds
 * the POINTS a fan earns: this bounds the taps themselves, and taps beyond it count for nothing
 * — not for the fan, not for the collective total. Both may be set; whichever binds first wins.
 *
 * NOT a rate limit: taps are never refused for arriving quickly, a fan simply has a finite
 * budget of them.
 */
export const tapQuotaEnabled = (maxTapsPerUser) => Number(maxTapsPerUser) > 0;

/**
 * The ceiling actually in force for the per-fan tap quota: its own hard maximum, or the
 * collective target when that is lower. Falls back to the hard maximum when no target is
 * resolved yet, so the field never advertises a bound that isn't known.
 */
export const tapQuotaCeiling = (target) => {
  const resolved = Number(target);
  return Number.isFinite(resolved) && resolved >= 1
    ? Math.min(REWARD_LIMITS.MAX_TAPS_PER_USER, resolved)
    : REWARD_LIMITS.MAX_TAPS_PER_USER;
};

/**
 * Blocks a per-fan tap quota set ABOVE the collective target.
 *
 * The target is the goal a whole channel taps toward, so a per-fan budget larger than it lets a
 * single fan fill the entire wave and the collective mechanic stops meaning anything.
 * `REWARD_LIMITS.MAX_TAPS_PER_USER` still bounds the field on its own; this is the tighter,
 * target-relative ceiling on top of it.
 *
 * Returns null when there is nothing to compare: the quota is off, the quota is out of range
 * (the plain range check owns that message), or no target is resolved — an empty per-wave target
 * inherits the channel's, so callers pass the EFFECTIVE target, not the raw field.
 */
export const tapQuotaTargetError = (rewards, target) => {
  if (!rewards?.tapQuotaEnabled) return null;
  const quota = Number(rewards.maxTapsPerUser);
  const ceiling = Number(target);
  if (!Number.isFinite(quota) || quota < 1) return null;
  if (!Number.isFinite(ceiling) || ceiling < 1) return null;
  if (quota <= ceiling) return null;
  return `Maximum taps per fan (${fmtCount(quota)}) cannot exceed the target of ${fmtCount(
    ceiling
  )} taps — one fan could fill the whole wave`;
};

/**
 * Post-FT window bounds, in MINUTES — the unit the API stores and accepts. Free-form, no
 * longer preset-only, and raised from 1440 (24h) to 4320 (3 days) alongside the backend's
 * `MAX_POST_FT_MINUTES`. The form lets an admin type the value in days / hours / minutes
 * (see DURATION_UNITS) and converts to minutes before validating and sending.
 */
export const POST_FT_BOUNDS = {
  MIN: 1,
  MAX: 4320
};

/**
 * Units the post-FT window may be ENTERED in. Minutes stay the wire format; these only change
 * how the number reads in the form, because "3 days" is far easier to sanity-check than "4320".
 * Ordered coarsest-first so `splitDuration` picks the most readable unit.
 */
export const DURATION_UNITS = [
  { key: 'days', label: 'Days', minutes: 1440 },
  { key: 'hours', label: 'Hours', minutes: 60 },
  { key: 'minutes', label: 'Minutes', minutes: 1 }
];

/** Minutes are the wire format, so they are also the fallback for anything unrecognised. */
const MINUTES_UNIT = DURATION_UNITS[DURATION_UNITS.length - 1];

const unitMinutes = (unit) =>
  DURATION_UNITS.find((u) => u.key === unit)?.minutes ?? MINUTES_UNIT.minutes;

/** ("3", "days") → 4320. Returns NaN for a non-numeric value so validation can report it. */
export const durationToMinutes = (value, unit) => Number(value) * unitMinutes(unit);

/**
 * 4320 → { value: 3, unit: 'days' }; 90 → { value: 90, unit: 'minutes' }.
 *
 * Picks the coarsest unit that divides the stored minutes EXACTLY, so a window an admin entered
 * as "2 days" comes back as "2 days" rather than "2880 minutes", while an odd value like 90 is
 * never rounded into a lie.
 */
export const splitDuration = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) {
    return { value: '', unit: 'minutes' };
  }
  // A non-integer divides nothing evenly (90.5 % 1 !== 0), so it falls through to minutes and
  // is shown verbatim for `validatePostFtMinutes` to reject, rather than being rounded silently.
  const unit = DURATION_UNITS.find((u) => n % u.minutes === 0) ?? MINUTES_UNIT;
  return { value: String(n / unit.minutes), unit: unit.key };
};

/** Game Day custom schedule bounds (match minutes). */
export const MATCH_MINUTE_BOUNDS = {
  MIN: 0,
  MAX: 150
};

/**
 * Which schedule a Game Day wave runs — mirrors the backend's `FanwaveScheduleKind`:
 *   DEFAULT — neither pair sent; the wave runs Half-Time → Full-Time + the channel's window.
 *   MINUTES — startMinute and/or endMinute, read off the live match clock.
 *   WINDOW  — a wall-clock schedule: `startsAt` + `endsAt`, or `endsAt` ALONE (the Half-Time
 *             start with a wall-clock end, which replaces Full-Time + X entirely). The wave
 *             appears and disappears on the wall clock regardless of the match, so it can open
 *             before kickoff or run past Full-Time. It stays a Game Day wave otherwise (bound
 *             match, predictions…). A `startsAt` with no `endsAt` is refused.
 *   MIXED   — one edge on each clock: a `startMinute` with an `endsAt`. Opens on the match clock,
 *             closes on the wall clock.
 *
 * MIXED is the newest of the four and the reason this is a list rather than a three-way switch. A
 * wave's two EDGES are chosen independently now (see anchors.js), so "open on the match clock,
 * close on the wall clock" is a schedule the API both accepts and reports — and anything that
 * still assumes three kinds reads it as MINUTES and drops its wall-clock close.
 */
export const GAME_DAY_SCHEDULE = {
  DEFAULT: 'default',
  MINUTES: 'minutes',
  WINDOW: 'window',
  MIXED: 'mixed'
};

const GAME_DAY_SCHEDULE_VALUES = Object.values(GAME_DAY_SCHEDULE);

/**
 * Which of the three schedules a Game Day list row is running.
 *
 * GET /list states it on the row (`schedule`) — the only reliable source once a wave is live,
 * because the row's `startsAt` then carries the observed activation time and a clock window is
 * no longer distinguishable from the default Half-Time open. Believe the row. A completed wave
 * states it too: the schedule is archived to history when the wave closes.
 *
 * The fallback below is for rows that don't carry it — an older API, or a run archived before
 * the schedule was persisted (unrecoverable, see `scheduleUnrecorded`). Only a clock window
 * stamps `startsAt` on a wave that hasn't activated, so a live wave falls back to DEFAULT and
 * the row shows its live times instead of claiming a schedule it can't prove.
 */
export const gameDayScheduleKind = (wave) => {
  if (!wave || wave.mode !== FANWAVE_MODE.GAME_DAY) return null;
  if (GAME_DAY_SCHEDULE_VALUES.includes(wave.schedule)) return wave.schedule;
  // Checked before MINUTES: a mixed wave HAS a start minute, so the minutes branch would claim
  // it and its wall-clock close would disappear from every label and edit form.
  if (wave.startMinute != null && wave.endsAt != null) return GAME_DAY_SCHEDULE.MIXED;
  if (wave.startMinute != null || wave.endMinute != null) return GAME_DAY_SCHEDULE.MINUTES;
  // A wave-of-its-own end date-time is a clock schedule on its own (the Half-Time start with a
  // wall-clock end), so it counts here even without a `startsAt`.
  if (wave.status === WAVE_STATUS.SCHEDULED && (wave.startsAt || wave.endsAt)) {
    return GAME_DAY_SCHEDULE.WINDOW;
  }
  return GAME_DAY_SCHEDULE.DEFAULT;
};

/**
 * The four timing shapes the create/edit form offers, which is one level finer than the three
 * schedule KINDS the API stores — `window` covers two of them:
 *
 *   DEFAULT         Half-Time → Full-Time + window     send nothing   schedule 'default'
 *   MINUTES         Half-Time → match minute M         endMinute      schedule 'minutes'
 *   HALF_TIME_UNTIL Half-Time → end date-time          endsAt only    schedule 'window'
 *   CLOCK_WINDOW    start date-time → end date-time    startsAt+endsAt schedule 'window'
 *
 * The first three all open on the MATCH clock, and a match may hold only one such wave — every
 * additional wave on the same match has to be a CLOCK_WINDOW (see `matchClockWaveOf`).
 */
export const WAVE_TIMING = {
  DEFAULT: 'default',
  MINUTES: 'minutes',
  HALF_TIME_UNTIL: 'half_time_until',
  /**
   * OPTION B — opens at the SECOND-HALF KICKOFF (read off the live match, like Half-Time is),
   * closes at a date-time the admin sets.
   *
   * The only shape that cannot be expressed with the four legacy schedule fields, so it is the
   * only one sent as an ANCHOR PAIR (`match_event: second_half` + `absolute`). Everything else on
   * the create form still travels as startMinute / endMinute / startsAt / endsAt.
   *
   * `gameDayTimingShape` CANNOT recover it from a stored wave: `anchorsToLegacy` keeps only the
   * absolute close, so a B wave comes back as `endsAt` alone and reads as HALF_TIME_UNTIL. No read
   * endpoint returns a wave's anchors, so the two are genuinely indistinguishable on load — which
   * is why the edit form asks which of the two opens the wave uses rather than assuming.
   */
  SECOND_HALF_UNTIL: 'second_half_until',
  /**
   * The half-time BREAK: opens at the whistle, closes when the feed reports the second-half
   * kick-off. This was option A until the client's dual-wave brief fixed Wave 1 at 15 minutes —
   * it is no longer offered on the create form, but waves already saved with it still read and
   * edit as this shape.
   *
   * Like option B it has no legacy representation (a bare `endsAt` means a wall-clock close), so
   * it travels as an anchor pair and takes no input from the admin at all.
   */
  HALF_TIME_INTERVAL: 'half_time_interval',
  /**
   * OPTION A — Game Day Fan Wave 1: opens at the Half-Time whistle and runs HALF_TIME_WAVE_MIN
   * (15) minutes from when it actually opened, or until the second-half kick-off if that comes
   * first. Fan Wave 2 (option B) then opens at the second-half kick-off.
   *
   * Sent as an anchor pair (`match_event: half_time` + `duration: 15, untilEvent: second_half`) —
   * no legacy field can say "close N minutes after opening" — and takes no input from the admin.
   */
  HALF_TIME_FIXED: 'half_time_fixed',
  CLOCK_WINDOW: 'clock_window',
  /**
   * MIXED — a match-minute open with a wall-clock close (schedule `mixed`).
   *
   * Reachable only through the anchor grammar, so it has no radio in the legacy create form: the
   * Timeline editor is where it is built and edited. It is listed here so that every read-only
   * surface DESCRIBES it correctly instead of falling through to MINUTES.
   */
  MIXED: 'mixed'
};

/**
 * Which timing shape a Game Day list row runs. Built on `gameDayScheduleKind` — i.e. read off
 * the row's stated `schedule`, never inferred from which fields happen to be set — and split
 * only where `window` is ambiguous:
 *
 * A wave that hasn't opened states its own `startsAt`, so an absent one means the Half-Time
 * start. Once the wave is LIVE the row's `startsAt` carries the observed activation time
 * instead, and the two window shapes become indistinguishable — which costs nothing, because a
 * running wave's start is locked either way and reads as the moment it opened.
 */
const ANCHORED_SHAPE = [
  [MATCH_EVENT.HALF_TIME, MATCH_EVENT.SECOND_HALF, WAVE_TIMING.HALF_TIME_INTERVAL],
  [MATCH_EVENT.HALF_TIME, MATCH_EVENT.FULL_TIME, WAVE_TIMING.DEFAULT]
];

/**
 * The shape an ANCHORED wave runs, read off its anchors rather than its `schedule`.
 *
 * Needed because `anchorsToLegacy` flattens several distinct waves onto the same legacy fields:
 * half-time → second-half and half-time → full-time both arrive with all four fields null, so
 * `schedule` says `default` for each and the edit form would reshape one into the other on save.
 *
 * Returns null for anchor pairs with no legacy equivalent (a signed offset, a duration, a
 * kick-off open) — those are built and edited in the Timeline, which reads the anchors directly.
 */
const anchoredTimingShape = (wave) => {
  const open = wave?.openAnchor;
  const close = wave?.closeAnchor;
  if (!open || !close) return null;

  if (open.kind === ANCHOR_KIND.ABSOLUTE && close.kind === ANCHOR_KIND.ABSOLUTE) {
    return WAVE_TIMING.CLOCK_WINDOW;
  }
  if (open.kind === ANCHOR_KIND.MATCH_MINUTE) {
    if (close.kind === ANCHOR_KIND.ABSOLUTE) return WAVE_TIMING.MIXED;
    if (close.kind === ANCHOR_KIND.MATCH_MINUTE) return WAVE_TIMING.MINUTES;
    return null;
  }
  if (open.kind !== ANCHOR_KIND.MATCH_EVENT) return null;
  // An offset moves the edge off the event, and no legacy shape can say that.
  if (Number(open.offsetMin) || 0) return null;

  // Option A. Only at exactly the fixed length: any other duration is a hand-built wave from the
  // Timeline, and reading it as this shape would let the edit form describe it as 15 minutes.
  if (
    close.kind === ANCHOR_KIND.DURATION &&
    open.event === MATCH_EVENT.HALF_TIME &&
    Number(close.durationMin) === HALF_TIME_WAVE_MIN &&
    close.untilEvent === MATCH_EVENT.SECOND_HALF
  ) {
    return WAVE_TIMING.HALF_TIME_FIXED;
  }
  if (close.kind === ANCHOR_KIND.ABSOLUTE) {
    if (open.event === MATCH_EVENT.SECOND_HALF) return WAVE_TIMING.SECOND_HALF_UNTIL;
    if (open.event === MATCH_EVENT.HALF_TIME) return WAVE_TIMING.HALF_TIME_UNTIL;
    return null;
  }
  if (close.kind !== ANCHOR_KIND.MATCH_EVENT) return null;
  const match = ANCHORED_SHAPE.find(([o, c]) => o === open.event && c === close.event);
  return match ? match[2] : null;
};

export const gameDayTimingShape = (wave) => {
  // For a wave that HAS anchors the mapping above is the final word, including when it answers
  // null. Falling through to the legacy inference would be worse than useless: those fields are
  // a flattened projection of the anchors, so an exotic wave (a signed offset, a duration) reads
  // as `default` and the edit form would seed itself with a wave this is not — then save it.
  if (wave?.openAnchor && wave?.closeAnchor) return anchoredTimingShape(wave);
  const kind = gameDayScheduleKind(wave);
  if (!kind) return null;
  if (kind === GAME_DAY_SCHEDULE.MINUTES) return WAVE_TIMING.MINUTES;
  if (kind === GAME_DAY_SCHEDULE.MIXED) return WAVE_TIMING.MIXED;
  if (kind !== GAME_DAY_SCHEDULE.WINDOW) return WAVE_TIMING.DEFAULT;
  return wave.startsAt ? WAVE_TIMING.CLOCK_WINDOW : WAVE_TIMING.HALF_TIME_UNTIL;
};

/**
 * Does this shape open on the match clock (Half-Time or a match minute) rather than a date?
 *
 * MIXED counts: only its CLOSE is on the wall clock. CLOCK_WINDOW is the one shape whose open is.
 */
export const startsOnMatchClock = (shape) => !!shape && shape !== WAVE_TIMING.CLOCK_WINDOW;

/**
 * Does this shape claim the Half-Time whistle? Every match-clock shape except option B, which
 * opens at the second-half kick-off. Kept apart from `startsOnMatchClock` because the dual-wave
 * workflow puts a Half-Time wave and an option B wave on the same match, in either creation order.
 */
export const opensAtHalfTime = (shape) =>
  startsOnMatchClock(shape) && shape !== WAVE_TIMING.SECOND_HALF_UNTIL;

/**
 * Kickoff-relative estimates, mirroring the backend's own (`FanwaveConstant`). Used ONLY to
 * order a match's waves and to warn about a default wave's fuzzy end — the live match clock
 * decides when a wave actually opens and closes.
 */
export const MATCH_CLOCK_ESTIMATE = {
  /** Half-Time, when no live signal says otherwise: kickoff + 45'. */
  HALF_TIME_AFTER_KICKOFF_MIN: 45,
  /** Wall-clock length of the interval — a 2nd-half minute sits this much later than its number. */
  HALF_TIME_BREAK_MIN: 15,
  /** Kickoff → the final whistle, wall clock (90' + interval + stoppage). */
  FULL_TIME_AFTER_KICKOFF_MIN: 120
};

const MS_PER_MIN = 60 * 1000;

/**
 * The second-half kickoff, projected off kickoff — what option B opens on before the match has
 * actually restarted.
 *
 * Half-time plus the interval: the wave opens on the live restart signal, so this is only ever an
 * ESTIMATE used to order waves, bound the end-time picker and check for overlap, exactly as the
 * Half-Time projection is. Returns null for a fixture with no kickoff time, which is also when the
 * API stops checking overlaps.
 */
export const secondHalfEstimate = (kickoff) => {
  const at = kickoff ? new Date(kickoff) : null;
  if (!at || Number.isNaN(at.getTime())) return null;
  return new Date(
    at.getTime() +
      (MATCH_CLOCK_ESTIMATE.HALF_TIME_AFTER_KICKOFF_MIN +
        MATCH_CLOCK_ESTIMATE.HALF_TIME_BREAK_MIN) *
        MS_PER_MIN
  );
};

const minuteToWallClock = (kickoff, minute) => {
  const breakOffset =
    minute > MATCH_CLOCK_ESTIMATE.HALF_TIME_AFTER_KICKOFF_MIN
      ? MATCH_CLOCK_ESTIMATE.HALF_TIME_BREAK_MIN
      : 0;
  return new Date(kickoff.getTime() + (minute + breakOffset) * MS_PER_MIN);
};

/**
 * Where one ANCHOR falls on the wall clock, measured from kickoff.
 *
 * Mirrors the API's own projection (`resolveAnchor` in lib/fanwave/anchors.ts) with one
 * difference that matters: the API prefers the match's OBSERVED timestamps where it has them,
 * and GET /list does not send those, so everything here is an estimate. That is enough for what
 * this drives — ordering a match's waves, spotting overlaps, and bounding the end-time picker —
 * none of which need the exact minute, only the right sequence.
 *
 * `previous` is the resolved OPEN, needed to place a `duration` close. Returns null when the
 * anchor cannot be placed at all.
 */
const projectAnchor = (anchor, kickoff, previous) => {
  if (!anchor?.kind || !kickoff) return null;
  switch (anchor.kind) {
    case ANCHOR_KIND.ABSOLUTE:
      return anchor.at ? new Date(anchor.at) : null;
    case ANCHOR_KIND.MATCH_MINUTE:
      return anchor.minute == null ? null : minuteToWallClock(kickoff, anchor.minute);
    case ANCHOR_KIND.DURATION: {
      if (!previous || anchor.durationMin == null) return null;
      const timed = new Date(previous.getTime() + anchor.durationMin * MS_PER_MIN);
      // Capped: the event closes it instead when it is due sooner, as the API resolves it.
      const cap = anchor.untilEvent
        ? projectAnchor({ kind: ANCHOR_KIND.MATCH_EVENT, event: anchor.untilEvent }, kickoff)
        : null;
      return cap && cap < timed ? cap : timed;
    }
    case ANCHOR_KIND.MATCH_EVENT: {
      const base = MATCH_EVENT_ESTIMATE_MIN[anchor.event];
      if (base == null) return null;
      const offset = Number(anchor.offsetMin) || 0;
      return new Date(kickoff.getTime() + (base + offset) * MS_PER_MIN);
    }
    default:
      return null;
  }
};

/**
 * Minutes from kickoff at which each match event is EXPECTED — the same figures the API projects
 * unobserved events from. Wall-clock minutes, not match minutes: the second-half numbers are
 * larger than their names because the interval sits between them.
 */
const MATCH_EVENT_ESTIMATE_MIN = {
  [MATCH_EVENT.KICKOFF]: 0,
  [MATCH_EVENT.HALF_TIME]: MATCH_CLOCK_ESTIMATE.HALF_TIME_AFTER_KICKOFF_MIN,
  [MATCH_EVENT.SECOND_HALF]:
    MATCH_CLOCK_ESTIMATE.HALF_TIME_AFTER_KICKOFF_MIN + MATCH_CLOCK_ESTIMATE.HALF_TIME_BREAK_MIN,
  [MATCH_EVENT.EXTRA_TIME]: MATCH_CLOCK_ESTIMATE.FULL_TIME_AFTER_KICKOFF_MIN,
  [MATCH_EVENT.PENALTIES]: MATCH_CLOCK_ESTIMATE.FULL_TIME_AFTER_KICKOFF_MIN + 30,
  [MATCH_EVENT.FULL_TIME]: MATCH_CLOCK_ESTIMATE.FULL_TIME_AFTER_KICKOFF_MIN
};

/**
 * A Game Day wave projected onto the wall clock, the same way the API decides whether two waves
 * on one match overlap: `{ start, end, startEstimated, endEstimated }`, with Dates or null.
 *
 * Anything the match clock decides is an ESTIMATE measured from kickoff — flagged as such so the
 * UI can say so rather than presenting a guess as a time. Returns nulls when the fixture has no
 * kickoff time yet (TBD), which is also when the API stops checking for overlaps.
 */
export const projectWaveSpan = (wave, postFtMinutes, kickoffOverride) => {
  const kickoffRaw = kickoffOverride ?? wave?.game?.date;
  const kickoff = kickoffRaw ? new Date(kickoffRaw) : null;
  const noKickoff = !kickoff || Number.isNaN(kickoff.getTime());
  const postFt = postFtMinutes ?? FANWAVE.DEFAULT_POST_FT_MINUTES;

  // A wave that carries ANCHORS is placed from them. The legacy branch below cannot see such a
  // wave at all: `anchorsToLegacy` on the API keeps only what those four fields express, so a
  // wave opening at the second-half kick-off arrives with no start whatsoever and the fallback
  // below places it at Half-Time. Two waves on one match then project to the SAME start, the
  // ordering tie-break falls through to creation order, and the list shows them in the wrong
  // sequence with the wrong one marked queued.
  if (wave?.openAnchor && wave?.closeAnchor) {
    const start = projectAnchor(wave.openAnchor, noKickoff ? null : kickoff);
    const end = projectAnchor(wave.closeAnchor, noKickoff ? null : kickoff, start);
    return {
      start,
      end,
      // Only a wall-clock edge is a stated time; everything else is measured off kickoff, and
      // the API resolves it against the real match once it gets there.
      startEstimated: wave.openAnchor.kind !== ANCHOR_KIND.ABSOLUTE,
      endEstimated: wave.closeAnchor.kind !== ANCHOR_KIND.ABSOLUTE
    };
  }

  let start = null;
  let startEstimated = false;
  if (wave?.startsAt) {
    start = new Date(wave.startsAt);
  } else if (!noKickoff) {
    start = minuteToWallClock(
      kickoff,
      wave?.startMinute ?? MATCH_CLOCK_ESTIMATE.HALF_TIME_AFTER_KICKOFF_MIN
    );
    startEstimated = true;
  }

  let end = null;
  let endEstimated = false;
  if (wave?.endsAt) {
    end = new Date(wave.endsAt);
  } else if (!noKickoff && wave?.endMinute != null) {
    end = minuteToWallClock(kickoff, wave.endMinute);
    endEstimated = true;
  } else if (!noKickoff) {
    end = new Date(
      kickoff.getTime() + (MATCH_CLOCK_ESTIMATE.FULL_TIME_AFTER_KICKOFF_MIN + postFt) * MS_PER_MIN
    );
    endEstimated = true;
  }

  return { start, end, startEstimated, endEstimated };
};

/**
 * Do two projected spans collide? Half-open, exactly as the API compares them: touching at a
 * boundary is fine (one ends 16:30, the next starts 16:30). A span missing either end can't be
 * placed at all, so it never reports a collision.
 */
export const spansOverlap = (a, b) =>
  !!(a?.start && a?.end && b?.start && b?.end) && a.start < b.end && a.end > b.start;

/**
 * The first of a match's waves whose projected span collides with `span` — the client-side twin
 * of the API's overlap rule, so the create form can rule an option out instead of letting the
 * admin discover it through a 400.
 *
 * Both sides are projections, so a wave the match clock drives is compared on the same kickoff
 * estimates the API uses. A fixture with no kickoff time can't be projected, and neither side
 * blocks anything then.
 */
export const findSpanConflict = (span, waves, postFtMinutes) =>
  (waves || []).find((wave) => spansOverlap(span, projectWaveSpan(wave, postFtMinutes))) || null;

/** Waves that can still run — the ones the API weighs when placing another wave on a match. */
export const isTerminalStatus = (status) =>
  status === WAVE_STATUS.COMPLETED ||
  status === WAVE_STATUS.CANCELLED ||
  status === WAVE_STATUS.EXPIRED;

/**
 * The match's wave that already opens on the MATCH clock, if it has one. At most one may exist
 * (the Half-Time whistle happens once and only one wave can hold the session), so its presence
 * is what forces every additional wave onto its own start and end date-times.
 */
export const matchClockWaveOf = (waves) =>
  (waves || []).find(
    (w) => !isTerminalStatus(w.status) && startsOnMatchClock(gameDayTimingShape(w))
  ) || null;

/**
 * A match's still-runnable waves in the order they open, earliest first. Waves the API can't
 * place (fixture with no kickoff time) keep their original order at the end.
 */
export const orderWavesByStart = (waves, postFtMinutes) =>
  (waves || [])
    .map((wave, index) => ({ wave, index, start: projectWaveSpan(wave, postFtMinutes).start }))
    .sort((a, b) => {
      if (!a.start && !b.start) return a.index - b.index;
      if (!a.start) return 1;
      if (!b.start) return -1;
      const diff = a.start.getTime() - b.start.getTime();
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map((entry) => entry.wave);

/**
 * The stretches of wall clock a match has NOT given away, earliest first — the windows another
 * wave on it can still be placed in. Each is `{ start, end, startEstimated, endEstimated }`, where
 * a null `start` means "anything before this" and a null `end` means open-ended, and the estimate
 * flags say the boundary comes off the match-clock projection rather than a time a wave states
 * itself — a distinction the UI has to admit rather than presenting a guess as a time.
 *
 * Waves the projection cannot place (a fixture with no kickoff time, so neither end is known) are
 * left out: the API stops checking overlaps for those too, so they take nothing away.
 */
export const freeGaps = (waves, postFtMinutes) => {
  const spans = orderWavesByStart(
    (waves || []).filter((wave) => !isTerminalStatus(wave.status)),
    postFtMinutes
  )
    .map((wave) => projectWaveSpan(wave, postFtMinutes))
    .filter((span) => span.start && span.end);

  const gaps = [];
  // The end of everything placed so far. Spans arrive in start order, so the gaps fall out of one
  // pass — with a `max` on the running end, since a wave fully inside another leaves no gap.
  let occupiedUntil = null;
  let untilEstimated = false;
  spans.forEach((span) => {
    if (!occupiedUntil || span.start > occupiedUntil) {
      gaps.push({
        start: occupiedUntil,
        end: span.start,
        startEstimated: !!occupiedUntil && untilEstimated,
        endEstimated: span.startEstimated
      });
    }
    if (!occupiedUntil || span.end > occupiedUntil) {
      occupiedUntil = span.end;
      untilEstimated = span.endEstimated;
    }
  });
  // What is left after the last wave closes — where a wave being added to a match usually goes.
  gaps.push({
    start: occupiedUntil,
    end: null,
    startEstimated: !!occupiedUntil && untilEstimated,
    endEstimated: false
  });
  return gaps;
};

/**
 * Which free window a moment falls in — the one a wave starting then would have to fit inside.
 *
 * Falls back to the LAST window when the moment lands in none of them: either nothing is typed
 * yet, or the time is one the match has already given away. Both are the same case for the form —
 * the admin is adding a wave after the ones already there, and that is the window to offer.
 */
export const freeGapAt = (gaps, at) => {
  const list = gaps || [];
  if (!list.length) return null;
  const holding = at
    ? list.find((gap) => (!gap.start || at >= gap.start) && (!gap.end || at < gap.end))
    : null;
  return holding || list[list.length - 1];
};

/**
 * Which of a match's waves are QUEUED — created, but waiting for the wave ahead of them to
 * close before the lifecycle cron can promote them onto the team's session (POST /create
 * answers `promoted: false` for exactly these).
 *
 * A queued wave is still `scheduled` like any other upcoming one, so the list can't read it off
 * the status: it is a wave with another still-runnable wave of the same match ahead of it. The
 * first one is the wave the match is currently on (live, or the next to open).
 *
 * Returns a Set of waveIds so the caller can ask row by row.
 */
export const queuedWaveIds = (waves, postFtMinutes) => {
  const runnable = orderWavesByStart(
    (waves || []).filter((w) => !isTerminalStatus(w.status)),
    postFtMinutes
  );
  const live = runnable.find((w) => w.status === WAVE_STATUS.ACTIVE);
  const holder = live || runnable[0];
  return new Set(runnable.filter((w) => w !== holder && w.waveId).map((w) => w.waveId));
};

/**
 * A finished Game Day run whose schedule was never recorded — archived before the API
 * persisted it, and unrecoverable now (the session that held it is long cleared).
 *
 * `gameDayScheduleKind` answers DEFAULT for these, which is what the form needs (it has to
 * offer *some* schedule), but read-only surfaces must not present the guess as fact: a wave
 * that actually ran on a clock window would read as the Half-Time default.
 */
export const scheduleUnrecorded = (wave) =>
  wave?.mode === FANWAVE_MODE.GAME_DAY &&
  wave?.status === WAVE_STATUS.COMPLETED &&
  !GAME_DAY_SCHEDULE_VALUES.includes(wave?.schedule);

/**
 * Which identifier deletes a wave-list row. DELETE /admin/fanwave/delete takes exactly one of
 * the two (sending both is a 400):
 *   waveId → the wave itself, in EITHER mode, with its history. Always prefer it: a match can
 *            hold several waves, and this is the only thing that tells them apart.
 *   gameId → the Game Day shorthand, and only a fallback for a legacy archive row that carries
 *            no waveId of its own. It addresses a match's wave only while the match holds
 *            exactly one; with several the API refuses it with a 409 asking for the waveId.
 */
export const deleteKeyOf = (wave) => {
  if (!wave) return null;
  if (wave.waveId) return { param: 'waveId', value: wave.waveId, kind: 'wave' };
  if (wave.gameId) return { param: 'gameId', value: wave.gameId, kind: 'game' };
  return null;
};

/** "12' → 80'" for a match-minute schedule, with the default side spelled out. */
export const matchMinutesLabel = (wave) => {
  const start = wave?.startMinute != null ? `${wave.startMinute}'` : 'Half-Time';
  const end = wave?.endMinute != null ? `${wave.endMinute}'` : 'Full-Time + window';
  return `${start} → ${end}`;
};

/**
 * How a Game Day wave's timing reads once it is settled — the edit form shows this instead of
 * the shape radios.
 *
 * A wave keeps the timing SHAPE it was created with: the shape decides how the wave is driven
 * (match clock vs wall clock vs the Half-Time default) and what its stored fields mean, so
 * switching it on an existing wave would reinterpret them. The times WITHIN a shape stay
 * editable until the wave opens.
 */
export const timingShapeLabel = (shape, postFtMinutes) => {
  if (shape === WAVE_TIMING.MINUTES) return 'Match minutes — read off the live match clock';
  if (shape === WAVE_TIMING.MIXED) {
    return 'Mixed — opens on a match minute, closes at a set time of day';
  }
  if (shape === WAVE_TIMING.HALF_TIME_UNTIL) {
    return 'Half-Time → a set time of day (replaces Full-Time + window)';
  }
  if (shape === WAVE_TIMING.SECOND_HALF_UNTIL) {
    return '2nd Half kickoff → a set time of day';
  }
  // Option A. Without its own line it fell through to the DEFAULT sentence below and every
  // read-only surface — the edit form's locked "Runs on" among them — announced a wave that
  // closes on the channel's post-Full-Time window, which is the one thing this shape does not do.
  if (shape === WAVE_TIMING.HALF_TIME_INTERVAL) {
    return 'The half-time break — Half-Time → 2nd Half kickoff';
  }
  if (shape === WAVE_TIMING.HALF_TIME_FIXED) {
    return `Fan Wave 1 — Half-Time → ${HALF_TIME_WAVE_MIN} minutes (or the 2nd Half kickoff, if sooner)`;
  }
  if (shape === WAVE_TIMING.CLOCK_WINDOW || shape === GAME_DAY_SCHEDULE.WINDOW) {
    return 'Clock window — times of day, ignoring the match clock';
  }
  const postFt = postFtMinutes ?? FANWAVE.DEFAULT_POST_FT_MINUTES;
  return `Default — Half-Time → Full-Time + ${minutesToHoursLabel(postFt) || `${postFt}m`}`;
};

/**
 * What a wave's close is READ OFF, when the live match is what decides it: "the second-half
 * kick-off", "match minute 80", "Full-Time + 5m".
 *
 * Answered from the wave's own SHAPE, never from which legacy field is missing — which is the
 * whole point. Three different shapes come back with no `endsAt` at all (the Half-Time default,
 * the half-time break, a match-minute wave), so anything that describes them by that absence
 * announces the default's post-Full-Time window for all three.
 *
 * Only for an ESTIMATED close (`projectWaveSpan().endEstimated`): a wave closing on the wall
 * clock states its own time, and a phrase would only get in the way of it.
 */
export const estimatedCloseLabel = (wave, postFtMinutes) => {
  const close = wave?.openAnchor && wave?.closeAnchor ? wave.closeAnchor : null;
  const postFtOffset =
    close?.kind === ANCHOR_KIND.MATCH_EVENT && close.event === MATCH_EVENT.FULL_TIME
      ? Number(close.offsetMin) || 0
      : 0;
  // The channel's window, said the way every other surface says it — the default schedule is
  // stored as `FT + postFtMinutes` (the API's own `legacyToAnchors`), so a wave that reads back
  // with anchors must not describe it differently from one that reads back with none.
  if (postFtOffset > 0) {
    return `Full-Time + ${minutesToHoursLabel(postFtOffset) || `${postFtOffset}m`}`;
  }
  if (close && close.kind !== ANCHOR_KIND.ABSOLUTE) {
    const sentence = anchorSentence(close).toLowerCase();
    // "closes at the second-half kick-off", but "closes at half-time": the two kick-offs are
    // things and take an article, the phases are times and don't. An offset or a minute reads as
    // a phrase already ("6 min before full-time", "match minute 80").
    const article =
      close.kind === ANCHOR_KIND.MATCH_EVENT &&
      !(Number(close.offsetMin) || 0) &&
      (close.event === MATCH_EVENT.KICKOFF || close.event === MATCH_EVENT.SECOND_HALF)
        ? 'the '
        : '';
    return `${article}${sentence}`;
  }
  if (!close && wave?.endMinute != null) return `match minute ${wave.endMinute}`;
  const postFt = postFtMinutes ?? FANWAVE.DEFAULT_POST_FT_MINUTES;
  return `Full-Time + ${minutesToHoursLabel(postFt) || `${postFt}m`}`;
};

/** Platform default reward rules — mirrors backend `DEFAULT_FANWAVE_REWARDS`. */
export const DEFAULT_REWARDS = {
  tapsPerCycle: 10,
  basePoints: 10,
  allowMultipleCycles: false,
  progressiveEnabled: false,
  milestones: [],
  headline: '',
  goalMessage: '',
  communityBonusEnabled: false,
  communityBonusPoints: 0,
  bonusEligibility: BONUS_ELIGIBILITY.ALL,
  capEnabled: false,
  maxPoints: 0,
  capBehavior: CAP_BEHAVIOR.CONTINUE,
  // Uncapped, so a wave configured before tap quotas existed keeps behaving exactly as it did.
  maxTapsPerUser: 0,
  rankRewardsEnabled: false,
  rankRewards: []
};

/**
 * Validates a free-form post-Full-Time window (minutes).
 * Returns an error message when invalid, or null when valid.
 */
export const validatePostFtMinutes = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || !Number.isInteger(n))
    return 'Window must be a whole number of minutes';
  if (n < POST_FT_BOUNDS.MIN) return `Window must be at least ${POST_FT_BOUNDS.MIN} minute`;
  if (n > POST_FT_BOUNDS.MAX)
    return `Window must be at most ${minutesToHoursLabel(POST_FT_BOUNDS.MAX)} (${
      POST_FT_BOUNDS.MAX
    } minutes)`;
  return null;
};

/**
 * "90" → "1h 30m", "4320" → "3d" — hint text next to the window input and the readout used
 * wherever a wave's close point is described.
 *
 * Carries days because the window now runs to 3 of them: without it a 3-day window read as
 * "72h", which is accurate but no easier to check than the raw 4320 it was meant to explain.
 * Only non-zero parts are shown, so round values stay short ("2d", not "2d 0h 0m").
 */
export const minutesToHoursLabel = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const d = Math.floor(n / 1440);
  const h = Math.floor((n % 1440) / 60);
  const m = n % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.join(' ');
};

/**
 * The reward fields that differ between `next` and `base` — used to build the partial
 * `rewards` payload so omitted (untouched) fields keep falling back to global/team defaults.
 * Arrays compare by JSON identity (order matters — tables are ordered in the UI).
 */
export const diffRewards = (base, next) => {
  const out = {};
  Object.keys(next).forEach((key) => {
    const a = base?.[key];
    const b = next[key];
    const changed = Array.isArray(b) ? JSON.stringify(a ?? []) !== JSON.stringify(b) : a !== b;
    if (changed) out[key] = b;
  });
  return Object.keys(out).length ? out : null;
};
